// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { TranslateService } from "@ngx-translate/core";
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Injectable({
  providedIn: "root",
})
export class StatementsService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private translateService: TranslateService
  ) {}

  /**
   * Create a new statements
   *
   * @since 1.0.0
   *
   * @param statement Statement data
   * @returns Observable<any>
   */
  create(statement: any): Observable<any> {
    return this.apiService.apiCall("POST", "statements", statement);
  }

  /**
   * Get statements list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(
    propertyId: string,
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", `statements/${propertyId}`, params);
  }
  
  /**
   * Get an exportation file of statements
   *
   * @since 1.0.0
   */
  getExport(propertyId: string): Observable<any> {
    return this.apiService
      .apiCall("GET", `statements/export/${propertyId}`, {}, { responseType: "blob" })
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }


  /**
   * Get given statementId data from back-end
   *
   * @since 1.0.0
   *
   * @param propertyId ID of the property to get
   * @param statementId ID of the statement to get
   */
  get(propertyId: string, statementId: string): Observable<any> {
    return this.apiService.apiCall("GET", `statements/${propertyId}/${statementId}`).pipe(
      catchError((err) => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update statement with given statementId with given statement data
   *
   * @since 1.0.0
   *
   * @param statementId ID of statement to update
   * @param data New balance's data
   */
   updateFields(propertyId: string, statementId: string, data: object): Observable<any> {
    return this.apiService.apiCall("PATCH", `statements/${propertyId}/${statementId}`, data);
  }

  /**
   * Delete permanently a statement
   *
   * @since 1.0.0
   *
   * @param statementId ID of the statement
   */
  delete(propertyId: string, statementId: string): Observable<any> {
    return this.apiService.apiCall("DELETE", `statements/${propertyId}/${statementId}`);
  }
}
